const ENV = (import.meta as any).env;

interface SatelliteEntry {
  clerkDomain: string;
}

interface CrossDomainConfig {
  primaryOrigin: string;
  defaultSatellite: string;
  satellites: Record<string, SatelliteEntry>;
}

function parseCrossDomainConfig(): CrossDomainConfig | null {
  try {
    const raw = ENV.VITE_APP_CROSS_DOMAIN_CONFIG;
    if (!raw) return null;
    return JSON.parse(raw) as CrossDomainConfig;
  } catch (e) {
    console.error('[CrossDomainConfig] Failed to parse VITE_APP_CROSS_DOMAIN_CONFIG:', e);
    return null;
  }
}

const config = parseCrossDomainConfig();
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const satelliteEntry = config?.satellites?.[hostname] ?? null;

/** True if the current hostname is a configured satellite */
export const isSatellite = !!satelliteEntry;

/** Clerk Frontend API domain for this satellite (e.g. "clerk.flexo.energy") */
export const clerkDomain = satelliteEntry?.clerkDomain ?? '';

/** Primary domain sign-in URL (e.g. "https://kwick-production.hivepower.tech/login") */
export const primarySignInUrl = config ? `${config.primaryOrigin}/login` : '';

/**
 * Where to redirect after auth on the primary domain.
 * Returns the satellite origin if we're on the primary, null otherwise.
 */
export function getPostAuthRedirectUrl(): string | null {
  if (!config) return null;
  // Don't redirect if we're already on a satellite
  if (isSatellite) return null;
  // Check redirect_url query param first (set by satellite redirect)
  const redirectUrl = new URLSearchParams(window.location.search).get('redirect_url');
  if (redirectUrl) return redirectUrl;
  // Fall back to default satellite
  if (config.defaultSatellite) {
    return `${config.defaultSatellite}/${ENV.VITE_APP_FIRST_PAGE || 'communities'}`;
  }
  return null;
}

/**
 * Returns the primary origin URL for invite links.
 * Uses the configured primaryOrigin, falling back to window.location.origin.
 */
export function getPrimaryOrigin(): string {
  return config?.primaryOrigin || window.location.origin;
}

/** The full parsed config (for debug logging) */
export const crossDomainConfig = config;
